import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import messagesHeroBg from '@/assets/messages-hero-bg.jpg';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MoreVertical, Phone, Video, Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useCallSystem } from '@/hooks/useCallSystem';
import { useUserPresence } from '@/hooks/useUserPresence';
import MessageBubble from '@/components/Messages/design/MessageBubble';
import ChatInput from '@/components/Messages/design/ChatInput';
import EmptyState from '@/components/Messages/design/EmptyState';
import AudioRecorder from '@/components/Messages/AudioRecorder';
import FileUploadModal from '@/components/Messages/FileUploadModal';
import SimpleCallInterface from '@/components/Messages/SimpleCallInterface';
interface Profile {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}
interface Conversation {
  id: string;
  name: string | null;
  type: 'direct' | 'group';
  last_message_at: string | null;
  participants: {
    user_id: string;
    profiles: Profile;
  }[];
}
interface Message {
  id: string;
  content: string | null;
  sender_id: string;
  created_at: string;
  message_type?: 'text' | 'file' | 'audio' | 'image';
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file_size?: number;
  media_duration?: number;
  sender: Profile;
}
const ChatView = () => {
  const {
    conversationId
  } = useParams();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const { isUserOnline } = useUserPresence();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [fileUploadModal, setFileUploadModal] = useState<{
    isOpen: boolean;
    type: 'file' | 'image';
  }>({
    isOpen: false,
    type: 'file'
  });

  // Get other participant for online status and calls
  const otherParticipant = conversation?.participants?.find(p => p.user_id !== user?.id);
  const isOtherUserOnline = otherParticipant ? isUserOnline(otherParticipant.user_id) : false;

  // Audio recording
  const audioRecorder = useAudioRecorder({
    onRecordingComplete: async (audioBlob, duration) => {
      if (conversationId && user) {
        await fileUpload.uploadAudio(audioBlob, duration);
      }
    },
    onError: error => {
      toast({
        title: "Errore registrazione",
        description: error,
        variant: "destructive"
      });
    }
  });

  // File upload
  const fileUpload = useFileUpload({
    conversationId: conversationId || '',
    userId: user?.id || '',
    onUploadComplete: async fileData => {
      await sendFileMessage(fileData);
      setFileUploadModal({
        isOpen: false,
        type: 'file'
      });
    }
  });

  // Call system - simplified and reliable
  const callSystem = useCallSystem({
    conversationId: conversationId || '',
    userId: user?.id || '',
    otherParticipant: otherParticipant ? {
      id: otherParticipant.user_id,
      name: otherParticipant.profiles?.display_name || otherParticipant.profiles?.username || 'Utente',
      avatar: otherParticipant.profiles?.avatar_url || undefined
    } : undefined
  });
  useEffect(() => {
    if (conversationId && user) {
      fetchConversation();
      fetchMessages();
      markAsRead();
    }
  }, [conversationId, user]);
  const fetchConversation = async () => {
    try {
      const {
        data: conversationData,
        error
      } = await supabase.from('conversations').select('*').eq('id', conversationId).single();
      if (error) throw error;

      // Get participants
      const {
        data: participantIds
      } = await supabase.from('conversation_participants').select('user_id').eq('conversation_id', conversationId).is('left_at', null);
      const participants = await Promise.all((participantIds || []).map(async p => {
        const {
          data: profile
        } = await supabase.from('profiles').select('user_id, username, display_name, avatar_url').eq('user_id', p.user_id).single();
        return {
          user_id: p.user_id,
          profiles: profile || {
            user_id: '',
            username: '',
            display_name: null,
            avatar_url: null
          }
        };
      }));
      setConversation({
        ...conversationData,
        type: conversationData.type as 'direct' | 'group',
        participants: participants
      });
    } catch (error) {
      console.error('Error fetching conversation:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare la conversazione",
        variant: "destructive"
      });
    }
  };
  const fetchMessages = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('messages').select('*').eq('conversation_id', conversationId).eq('is_deleted', false).order('created_at', {
        ascending: true
      });
      if (error) throw error;
      const messagesWithSenders = await Promise.all((data || []).map(async msg => {
        const {
          data: profile
        } = await supabase.from('profiles').select('user_id, username, display_name, avatar_url').eq('user_id', msg.sender_id).single();

        // Prepare signed URLs for private buckets
        let signedFileUrl: string | null = msg.file_url;
        try {
          if (msg.file_url && msg.message_type) {
            const bucket = msg.message_type === 'audio' ? 'chat-audio' : msg.message_type === 'image' || msg.message_type === 'file' ? 'chat-files' : null;
            if (bucket) {
              let path = msg.file_url as string;
              // Extract path if a full URL was stored
              const publicMarker = `/object/public/${bucket}/`;
              if (path.startsWith('http')) {
                if (path.includes(publicMarker)) {
                  path = path.split(publicMarker)[1];
                } else {
                  // Try generic split by bucket name
                  const idx = path.indexOf(`${bucket}/`);
                  if (idx !== -1) {
                    path = path.substring(idx + bucket.length + 1);
                  }
                }
              }
              const {
                data: signed,
                error: signErr
              } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60);
              if (!signErr && signed?.signedUrl) {
                signedFileUrl = signed.signedUrl;
              }
            }
          }
        } catch (e) {
          console.warn('Signed URL generation failed', e);
        }
        return {
          ...msg,
          file_url: signedFileUrl,
          message_type: msg.message_type as 'text' | 'file' | 'audio' | 'image' || 'text',
          sender: profile || {
            user_id: '',
            username: '',
            display_name: null,
            avatar_url: null
          }
        } as Message;
      }));
      setMessages(messagesWithSenders);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i messaggi",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      markAsRead();
    }
  };
  const markAsRead = async () => {
    if (!conversationId || !user) return;
    try {
      const {
        error
      } = await supabase.from('conversation_participants').update({
        last_read_at: new Date().toISOString()
      }).eq('conversation_id', conversationId).eq('user_id', user.id);
      if (error) {
        console.error('Error marking as read:', error);
      } else {
        console.log('Messages marked as read for conversation:', conversationId);
      }
    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  };
  const sendMessage = async () => {
    if (!newMessage.trim() || !conversationId) return;
    try {
      const {
        error
      } = await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: user?.id,
        content: newMessage.trim(),
        message_type: 'text'
      });
      if (error) throw error;
      setNewMessage('');
      fetchMessages();
      markAsRead();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Errore",
        description: "Impossibile inviare il messaggio",
        variant: "destructive"
      });
    }
  };
  const sendFileMessage = async (fileData: {
    url: string;
    name: string;
    type: string;
    size: number;
    duration?: number;
  }) => {
    if (!conversationId) return;
    try {
      let messageType: 'file' | 'image' | 'audio' = 'file';
      if (fileData.type.startsWith('image/')) {
        messageType = 'image';
      } else if (fileData.type.startsWith('audio/')) {
        messageType = 'audio';
      }
      const {
        error
      } = await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: user?.id,
        content: messageType === 'file' ? `📎 ${fileData.name}` : '',
        message_type: messageType,
        file_url: fileData.url,
        file_name: fileData.name,
        file_type: fileData.type,
        file_size: fileData.size,
        media_duration: fileData.duration || null
      });
      if (error) throw error;
      fetchMessages();
      markAsRead();
    } catch (error) {
      console.error('Error sending file message:', error);
      toast({
        title: "Errore",
        description: "Impossibile inviare il file",
        variant: "destructive"
      });
    }
  };
  const handleFileUpload = (file: File) => {
    fileUpload.uploadFile(file, false);
  };

  // Simplified call handlers using the new call system
  const handleStartCall = (isVideo: boolean) => {
    callSystem.startCall(isVideo);
  };
  const getConversationName = () => {
    if (!conversation) return 'Chat';
    if (conversation.type === 'group') {
      return conversation.name || 'Chat di gruppo';
    }
    const otherParticipant = conversation.participants?.find(p => p.user_id !== user?.id);
    return otherParticipant?.profiles?.display_name || otherParticipant?.profiles?.username || 'Chat';
  };
  const getConversationAvatar = () => {
    if (!conversation || conversation.type === 'group') {
      return null;
    }
    const otherParticipant = conversation.participants?.find(p => p.user_id !== user?.id);
    return otherParticipant?.profiles?.avatar_url;
  };
  if (!conversationId) {
    navigate('/app/messages');
    return null;
  }
  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return 'Oggi';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ieri';
    } else {
      return date.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'short'
      });
    }
  };
  const shouldShowDateSeparator = (currentMessage: Message, previousMessage: Message | null) => {
    if (!previousMessage) return true;
    const currentDate = new Date(currentMessage.created_at).toDateString();
    const previousDate = new Date(previousMessage.created_at).toDateString();
    return currentDate !== previousDate;
  };
  const groupedMessages = messages.reduce((groups: Message[][], message, index) => {
    const previousMessage = messages[index - 1];
    if (!previousMessage || previousMessage.sender_id !== message.sender_id || new Date(message.created_at).getTime() - new Date(previousMessage.created_at).getTime() > 300000) {
      groups.push([message]);
    } else {
      groups[groups.length - 1].push(message);
    }
    return groups;
  }, []);

  // Block main page scroll when chat is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  return <div className="relative z-30 flex flex-col bg-background h-[calc(100vh-128px)] md:h-[calc(100vh-64px)] overflow-hidden">
      <style>{`
        amp-auto-ads { display: none !important; }
        .adsbygoogle { display: none !important; }
      `}</style>
      
      {/* Background overlay extending under the header */}
      <div className="absolute inset-0 cosmic-background" style={{
      backgroundImage: `url(${messagesHeroBg})`
    }} />
      
      {/* Chat Header */}
      <header className="relative glass border-b border-border/30 backdrop-blur-xl px-3 md:px-4 py-2 flex-shrink-0 z-10">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/messages')} className="h-8 w-8 p-0 hover:bg-primary/10 transition-all rounded-full">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="relative">
              <Avatar className="h-8 w-8 ring-2 ring-primary/10">
                <AvatarImage src={getConversationAvatar() || ''} />
                <AvatarFallback className="gradient-primary text-primary-foreground text-xs font-medium">
                  {conversation?.type === 'group' ? <Users className="w-3 h-3" /> : getConversationName()[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {conversation?.type !== 'group' && isOtherUserOnline && (
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-background"></div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground truncate text-sm">
                {getConversationName()}
              </h3>
              <p className="text-xs text-muted-foreground flex items-center">
                {conversation?.type === 'group' ? (
                  <span>{conversation.participants?.length || 0} membri</span>
                ) : (
                  <span className="flex items-center">
                    <div className={`w-1.5 h-1.5 rounded-full mr-1 ${isOtherUserOnline ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    {isOtherUserOnline ? 'Online' : 'Offline'}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-1">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/10 transition-all rounded-full" onClick={() => handleStartCall(false)}>
              <Phone className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/10 transition-all rounded-full" onClick={() => handleStartCall(true)}>
              <Video className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/10 transition-all rounded-full">
              <MoreVertical className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Messages Area - Only this section scrolls */}
      <div className="relative flex-1 min-h-0 overflow-y-auto px-3 md:px-4 z-10 py-[11px]">
        <div className="w-full lg:max-w-4xl lg:mx-auto space-y-3">
          {loading ? <div className="flex items-center justify-center h-full min-h-[300px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div> : messages.length === 0 ? <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
              <EmptyState type="no-messages" />
            </div> : <>
              {groupedMessages.map((group, groupIndex) => {
            const showDateSeparator = shouldShowDateSeparator(group[0], groupIndex > 0 ? groupedMessages[groupIndex - 1][groupedMessages[groupIndex - 1].length - 1] : null);
            return <div key={`group-${groupIndex}`} className="space-y-1">
                    {showDateSeparator && <div className="flex justify-center my-4">
                        <div className="glass px-3 py-1.5 rounded-full border border-border/30">
                          <span className="text-xs text-muted-foreground font-medium">
                            {formatDate(group[0].created_at)}
                          </span>
                        </div>
                      </div>}
                    
                    <div className="space-y-1">
                      {group.map((message, messageIndex) => <MessageBubble key={message.id} content={message.content || ''} isCurrentUser={message.sender_id === user?.id} senderName={message.sender_id !== user?.id ? message.sender.display_name || message.sender.username : undefined} senderAvatar={message.sender_id !== user?.id ? message.sender.avatar_url : undefined} timestamp={formatTime(message.created_at)} showAvatar={messageIndex === group.length - 1 && message.sender_id !== user?.id} isFirstInGroup={messageIndex === 0} isLastInGroup={messageIndex === group.length - 1} messageType={message.message_type} fileUrl={message.file_url} fileName={message.file_name} fileType={message.file_type} fileSize={message.file_size} mediaDuration={message.media_duration} />)}
                    </div>
                  </div>;
          })}
            </>}
        </div>
      </div>

      {/* Chat Input - sticks to bottom */}
      <footer className="relative flex-shrink-0 border-t border-border/30 bg-background/95 backdrop-blur-xl z-10 pb-0">
        <div className="w-full px-3 md:px-4 lg:max-w-4xl lg:mx-auto py-2 my-[13px]">
          <ChatInput value={newMessage} onChange={setNewMessage} onSend={sendMessage} placeholder="Scrivi un messaggio..." onAttachFile={() => setFileUploadModal({
          isOpen: true,
          type: 'file'
        })} onAttachImage={() => setFileUploadModal({
          isOpen: true,
          type: 'image'
        })} onStartRecording={audioRecorder.startRecording} isRecording={audioRecorder.isRecording} recordingComponent={<AudioRecorder isRecording={audioRecorder.isRecording} duration={audioRecorder.duration} onStartRecording={audioRecorder.startRecording} onStopRecording={audioRecorder.stopRecording} onCancelRecording={audioRecorder.cancelRecording} formatDuration={audioRecorder.formatDuration} disabled={fileUpload.uploading} />} />
        </div>
      </footer>
      
      {/* File Upload Modal */}
      <FileUploadModal isOpen={fileUploadModal.isOpen} onClose={() => setFileUploadModal({
      isOpen: false,
      type: 'file'
    })} onFileSelect={handleFileUpload} uploading={fileUpload.uploading} type={fileUploadModal.type} />

      {/* Simple Call Interface - replaces complex call system */}
      <SimpleCallInterface callState={callSystem.callState} incomingCall={callSystem.incomingCall} localStream={callSystem.localStream} remoteStream={callSystem.remoteStream} onAcceptCall={callSystem.acceptCall} onRejectCall={callSystem.rejectCall} onEndCall={callSystem.endCall} />
    </div>;
};
export default ChatView;